import { useEffect } from 'react';

import { useDesktopPermissionGate } from '@/app-shell/rpa/desktop-permission-gate';
import { logger } from '@/utils/logger/logger';
import { rpaRecorderBridge, type RpaRecorderBridge } from '@heidi/plugin-rpa';

const SUBSCRIPTION_RETRY_MS = 1_000;
// Rust drops a request once it is acknowledged, so a settled id is never
// re-emitted; the set only needs to absorb late duplicate deliveries. Cap it
// so a long-lived main window does not accumulate one id per agent call.
const SETTLED_REQUEST_CAP = 1_000;

/**
 * Answers agent permission requests through the existing recording permission
 * gate. Only the main window hosts the gate; Rust rejects acks from other
 * webviews. Missing permissions open the gate modal and ack `granted: false`
 * so the tool fails stable while the user can grant and the agent can retry.
 */
export function useAgentRecordingPermissionBridge(
  bridge: RpaRecorderBridge = rpaRecorderBridge
): void {
  const { ensurePermissions } = useDesktopPermissionGate();

  useEffect(() => {
    let disposed = false;
    let unlisten: (() => void) | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const requestsInFlight = new Set<string>();
    const settledRequests = new Set<string>();
    const markSettled = (requestId: string) => {
      settledRequests.add(requestId);
      if (settledRequests.size > SETTLED_REQUEST_CAP) {
        const oldest = settledRequests.values().next().value;
        if (oldest !== undefined) settledRequests.delete(oldest);
      }
    };

    const subscribe = async (): Promise<void> => {
      try {
        const dispose = await bridge.subscribeAgentPermissionRequested((request) => {
          // An event queued before teardown must not open the gate.
          if (
            disposed ||
            requestsInFlight.has(request.requestId) ||
            settledRequests.has(request.requestId)
          )
            return;
          requestsInFlight.add(request.requestId);
          void (async () => {
            try {
              let granted = false;
              try {
                granted = await ensurePermissions('recording');
              } catch (error) {
                logger.warn('[rpa-recorder] agent permission preflight failed', {
                  error,
                  requestId: request.requestId,
                });
              }
              if (disposed) return;
              const result = await bridge.acknowledgeAgentPermission({
                ...request,
                granted,
              });
              if (result.ok) {
                markSettled(request.requestId);
              } else {
                // Stale (capture cancelled while the gate was open) or rejected:
                // the executor has already moved on, so this is log-only.
                logger.warn('[rpa-recorder] agent permission ack rejected', {
                  code: result.error.code,
                  requestId: request.requestId,
                });
                if (result.error.code !== 'UNKNOWN') {
                  markSettled(request.requestId);
                }
              }
            } finally {
              requestsInFlight.delete(request.requestId);
            }
          })();
        });
        if (disposed) dispose();
        else unlisten = dispose;
      } catch (error) {
        if (disposed) return;
        logger.warn('[rpa-recorder] agent permission subscription failed; retrying', {
          error,
        });
        retryTimer = setTimeout(() => void subscribe(), SUBSCRIPTION_RETRY_MS);
      }
    };

    void subscribe();

    return () => {
      disposed = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      unlisten?.();
    };
  }, [bridge, ensurePermissions]);
}
